import type { ArtCredit, DatasetCredit, SoftwareCredit } from './data/creditTypes';
import { escapeHtml } from './format';

/**
 * The credits (review M62, the user's word: *"credits page"*; shown in the
 * game menu since M63: *"put a credit button in there which shows the
 * credits"*): every dataset the game carries, with its citation, its
 * license and the credit its terms ask, every work of art it carries with
 * its credit (M75), and every package it ships with its license's notice in
 * full (`src/ui/data/credits.ts`, generated from the archives' headers and
 * the art's records by `scripts/credits.ts`). An address opens in a new tab,
 * so following one never leaves the game.
 */

/** A web address in a credit's text: up to a space, a bracket or a quote, and not its sentence's last stop. */
const ADDRESS = /https?:\/\/[^\s<>"']*[^\s<>"'.,;:)]/g;

/**
 * Text made safe, its web addresses made links: each found in the text as
 * written and every piece escaped after (review M62's reviewer: found in the
 * escaped text, PSL's address followed by a quote took in `&quot` and
 * linked nowhere).
 */
function linked(text: string): string {
  let out = '';
  let from = 0;
  for (const m of text.matchAll(ADDRESS)) {
    const url = escapeHtml(m[0]);
    out += `${escapeHtml(text.slice(from, m.index))}<a href="${url}" target="_blank" rel="noopener">${url}</a>`;
    from = m.index + m[0].length;
  }
  return out + escapeHtml(text.slice(from));
}

function dataset(d: DatasetCredit): string {
  return `<section><h4>${escapeHtml(d.dataset)}</h4><dl>`
    + `<dt>Citation</dt><dd>${linked(d.citation)}</dd>`
    + `<dt>License</dt><dd>${linked(d.license)}</dd>`
    + `<dt>As its terms ask</dt><dd>${linked(d.owes)}</dd></dl></section>`;
}

function artwork(a: ArtCredit): string {
  return `<section><h4>${escapeHtml(a.title)}</h4><p>${escapeHtml(a.credit)}</p></section>`;
}

function software(s: SoftwareCredit): string {
  return `<section><h4>${escapeHtml(s.name)} ${escapeHtml(s.version)}</h4><p>${escapeHtml(s.license)}</p><pre>${escapeHtml(s.notice)}</pre></section>`;
}

/** The credits' markup, for the menu's credits view. */
export function creditsHtml(datasets: readonly DatasetCredit[], packages: readonly SoftwareCredit[], art: readonly ArtCredit[] = []): string {
  return `<h2>Credits</h2>
    <p>The game is built from these datasets, this art and this software. Each dataset was cut to the places, depths and months the
    game reads, averaged, and in places fitted to build its tables; each is credited here as its own terms ask. No agency,
    program, institution or author named here endorses the game.</p>
    <h3>Data (${datasets.length})</h3>${datasets.map(dataset).join('')}
    <h3>Art (${art.length})</h3>${art.map(artwork).join('')}
    <h3>Software (${packages.length})</h3>${packages.map(software).join('')}`;
}
